import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { ROLE } from '@/shared/api/borrow';
import { useRefreshAgreements } from '@/shared/hooks/useRefreshAgreements';
import { toast } from '@/shared/hooks/useToast';

interface UseHandleSupplyClickProps {
    leverage: any;
    address: string;
    currentApy: number;
    totalDeposited: string;
    totalDepositThreshold: string;
    agreement: any;
    role: ROLE;
}

export const useHandleSupplyClick = ({
    leverage,
    address,
    currentApy,
    totalDeposited,
    totalDepositThreshold,
    agreement,
    role,
}: UseHandleSupplyClickProps) => {
    const dispatch = useDispatch();
    const refreshAgreements = useRefreshAgreements(role);

    return useCallback(() => {
        if (!leverage) {
            toast({
                title: 'There was an error fetching leverage data',
                variant: 'error',
            });
            return;
        }

        if (!leverage.userBalance) {
            refreshAgreements?.();
            toast({
                title: 'There was an error fetching leverage balance',
                variant: 'error',
            });
            return;
        }

        if (!totalDepositThreshold) {
            refreshAgreements?.();
            toast({
                title: 'There was an error fetching total supply',
                variant: 'error',
            });
            return;
        }

        if (agreement.version === 2) {
            return dispatch(
                showModalAction({
                    modalName: ModalName.POOLS_SUPPLY_V2_MODAL,
                    modalTitle: 'Supply Liquidity',
                    modalProps: {
                        tokenSymbol: leverage.symbol,
                        balance: leverage.userBalance,
                        currentApy,
                        totalDepositThreshold,
                        agreementAddress: address,
                        totalBorrowed: agreement.totalBorrowed,
                        tokenAddress: leverage.address,
                        totalAssets: agreement.totalAssets,
                        assetsFetched: agreement.assetsFetched,
                        isSupply: true,
                        decimals: leverage.decimals,
                        refresh: refreshAgreements,
                        poolUtilization: agreement.poolUtilization,
                        netApy: agreement.netApy,
                    },
                }),
            );
        }

        dispatch(
            showModalAction({
                modalName: ModalName.POOLS_SUPPLY_MODAL,
                modalTitle: 'Supply Liquidity',
                modalProps: {
                    tokenSymbol: leverage.symbol,
                    balance: leverage.userBalance,
                    currentApy,
                    totalDeposited,
                    totalDepositThreshold,
                    agreementAddress: address,
                    tokenAddress: leverage.address,
                    isSupply: true,
                    decimals: leverage.decimals,
                    refresh: refreshAgreements,
                    poolUtilization: agreement.poolUtilization,
                    netApy: agreement.netApy,
                },
            }),
        );
    }, [
        dispatch,
        leverage,
        address,
        currentApy,
        totalDeposited,
        totalDepositThreshold,
        agreement,
        refreshAgreements,
    ]);
};
